import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import InfoTooltip from 'components/InfoTooltip';
import FullScreenLoading from 'components/shared/Utils/FullScreenLoading/FullScreenLoading';
import ActivateContainer from 'routes/App/feedback/components/Activate/ActivateContainer';
import SoonContainer from 'routes/App/feedback/components/Activate/SoonContainer';
import {FeedbackInner} from 'routes/App/feedback/styles';
import {FIRST_SURVEY_PRODUCTION_DATE} from 'routes/App/feedback/constants';
import {GET_FEEDBACK_DATA, SET_FILTER} from 'store/feedback/action';
import normalizePercentageByDecimal from 'helpers/functionUtils/normalizePercentageByDecimal';
import IconAverageRating from 'assets/star.svg';
import IconChevronDown from 'assets/chevron.svg';
import useOutsideClick from 'hooks/useOutsideClick';
import computePopularFromSurveyAnswers, {
  computeFeedbackByRating
} from './popularAnswers';
import {
  AnswerBarFill,
  AnswerBarTrack,
  AnswerCount,
  AnswerDot,
  AnswerRow,
  AnswersList,
  AnswerText,
  AnswerContentRow,
  BarChart,
  CardHeader,
  CardSubtitle,
  CardTitle,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  DropdownTrigger,
  EmptyMessage,
  FilterGroup,
  FilterLabel,
  FiltersRow,
  InsightsCard,
  InsightsRoot,
  Legend,
  LegendDot,
  LegendItem,
  MetricItem,
  MetricLabel,
  MetricLabelRow,
  MetricValue,
  PerformanceMetrics,
  RatingBadge,
  RatingBar,
  RatingBarFill,
  RatingCardHeader,
  RatingCardTotal,
  RatingItem,
  RatingItemHeader,
  RatingItemMetric,
  RatingItemText,
  RatingList,
  SatisfactionHeader,
  SectionTitle,
  StarBar,
  StarBarCount,
  StarBarFill,
  StarBarLabel,
  StarBarPercent,
  StarBarTrack,
  TwoColumn
} from './insightsStyles';

const TIME_PERIOD_OPTIONS = [
  {id: 'all_time', label: 'All time'},
  {id: 'last_30', label: 'Last 30 days'},
  {id: 'last_90', label: 'Last 90 days'},
  {id: 'this_year', label: 'This year'}
];

const ORDER_TYPE_OPTIONS = [
  {id: 0, label: 'All'},
  {id: 1, label: 'Delivery'},
  {id: 2, label: 'Pickup'}
];

const TOOLTIPS = {
  smsSent:
    'Number of post-order survey messages sent to your customers by SMS.',
  views:
    'Numbers of unique customers who clicked the link on SMS and visited the survey page.',
  completed: 'Number of completed post-order surveys.',
  completionRate: 'Percentage of completed surveys out of total sent surveys.',
  gmb:
    'Customers who reached your Google Review page after completing the survey. More positive reviews can increase visibility, orders, and search rankings.'
};

const InfoMetric = ({label, value, tooltip}) => (
  <MetricItem>
    <MetricValue>{value}</MetricValue>
    <MetricLabelRow>
      <MetricLabel>{label}</MetricLabel>
      <InfoTooltip content={tooltip} />
    </MetricLabelRow>
  </MetricItem>
);

InfoMetric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tooltip: PropTypes.string.isRequired
};

const RatingSubCard = ({title, description, total, items}) => (
  <InsightsCard>
    <RatingCardHeader>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{description}</CardSubtitle>
      </CardHeader>
      <RatingCardTotal>Total: {total}</RatingCardTotal>
    </RatingCardHeader>
    <RatingList>
      {items.map((item) => {
        const percent = total > 0 ? Math.round((item.count / total) * 100) : 0;
        return (
          <RatingItem key={item.text}>
            <RatingItemHeader>
              <RatingItemText>{item.text}</RatingItemText>
              <RatingItemMetric>
                {item.count}/{total} ({percent}%)
              </RatingItemMetric>
            </RatingItemHeader>
            <RatingBar>
              <RatingBarFill style={{width: `${Math.min(percent, 100)}%`}} />
            </RatingBar>
          </RatingItem>
        );
      })}
    </RatingList>
  </InsightsCard>
);

RatingSubCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({text: PropTypes.string, count: PropTypes.number})
  ).isRequired
};

const InsightsContent = () => {
  const dispatch = useDispatch();
  const [timePeriodId, setTimePeriodId] = useState('last_30');
  const [timeOpen, setTimeOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const timeRef = useRef(null);
  const orderRef = useRef(null);
  useOutsideClick(timeRef, () => setTimeOpen(false), timeOpen);
  useOutsideClick(orderRef, () => setOrderOpen(false), orderOpen);

  const tabIndex = useSelector(
    (state) => state.feedbackReducer.filters.tabIndex
  );
  const loading = useSelector((state) => state.feedbackReducer.loading);
  const data = useSelector((state) => state.feedbackReducer.data);
  const locationId = useSelector((state) => state.user?.activeLocation?._id);
  const activeLocation = useSelector((state) => state.user?.activeLocation);
  const Timezone = activeLocation?.Timezone;

  useEffect(() => {
    dispatch(GET_FEEDBACK_DATA());
  }, [tabIndex, locationId]);

  const applyTimePeriod = (id) => {
    setTimePeriodId(id);
    setTimeOpen(false);
    const now = moment()
      .utcOffset(Timezone || 0, false)
      .utc();
    let startDate;
    const endDate = now.clone().endOf('isoWeek').format();
    if (id === 'last_30') {
      startDate = now.clone().subtract(30, 'days').format();
    } else if (id === 'last_90') {
      startDate = now.clone().subtract(90, 'days').format();
    } else if (id === 'this_year') {
      startDate = now.clone().startOf('year').format();
    } else {
      startDate = FIRST_SURVEY_PRODUCTION_DATE;
    }
    dispatch(SET_FILTER({startDate, endDate}));
    dispatch(GET_FEEDBACK_DATA());
  };

  const applyOrderType = (id) => {
    setOrderOpen(false);
    if (id !== tabIndex) {
      dispatch(SET_FILTER({tabIndex: id}));
    }
  };

  if (loading || !data) {
    return <FullScreenLoading />;
  }

  const {
    SelectedTypeSurveysCount = 0,
    Views = 0,
    CompletedSurveys = 0,
    GMBReviewClicks = 0,
    StarsTotalCount = 0,
    OneStarsCount = 0,
    TwoStarsCount = 0,
    ThreeStarsCount = 0,
    FourStarsCount = 0,
    FiveStarsCount = 0,
    AverageSatisfactionRate = 0,
    PopularSurveyAnswers = [],
    SurveyAnswers = [],
    BadRankingTotal = 0,
    GoodRankingTotal = 0
  } = data;

  const isAllOrderType = tabIndex === 0;
  const popularAnswers = isAllOrderType
    ? PopularSurveyAnswers
    : computePopularFromSurveyAnswers(SurveyAnswers, tabIndex);
  const feedbackByRating = isAllOrderType
    ? null
    : computeFeedbackByRating(SurveyAnswers, tabIndex);

  const completionRate =
    Views > 0 ? Math.round((CompletedSurveys / Views) * 100) : 0;

  const starPercents = normalizePercentageByDecimal([
    StarsTotalCount ? (OneStarsCount / StarsTotalCount) * 100 : 0,
    StarsTotalCount ? (TwoStarsCount / StarsTotalCount) * 100 : 0,
    StarsTotalCount ? (ThreeStarsCount / StarsTotalCount) * 100 : 0,
    StarsTotalCount ? (FourStarsCount / StarsTotalCount) * 100 : 0,
    StarsTotalCount ? (FiveStarsCount / StarsTotalCount) * 100 : 0
  ]);

  const starRows = [
    {label: 1, percent: starPercents[0], count: OneStarsCount},
    {label: 2, percent: starPercents[1], count: TwoStarsCount},
    {label: 3, percent: starPercents[2], count: ThreeStarsCount},
    {label: 4, percent: starPercents[3], count: FourStarsCount},
    {label: 5, percent: starPercents[4], count: FiveStarsCount}
  ];

  const answers = popularAnswers.slice(0, 4);
  const totalAnswers = answers.reduce((sum, a) => sum + (a.Count || 0), 0);

  const timePeriodLabel = TIME_PERIOD_OPTIONS.find((o) => o.id === timePeriodId)
    .label;
  const orderTypeLabel = ORDER_TYPE_OPTIONS.find((o) => o.id === tabIndex)
    .label;

  return (
    <InsightsRoot>
      <FiltersRow>
        <FilterGroup>
          <FilterLabel>Time period</FilterLabel>
          <Dropdown ref={timeRef}>
            <DropdownTrigger
              type='button'
              onClick={() => setTimeOpen((v) => !v)}
              aria-expanded={timeOpen}
            >
              {timePeriodLabel}
              <img src={IconChevronDown} alt='' />
            </DropdownTrigger>
            {timeOpen && (
              <DropdownMenu>
                {TIME_PERIOD_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.id}
                    type='button'
                    className={cn({isActive: opt.id === timePeriodId})}
                    onClick={() => applyTimePeriod(opt.id)}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            )}
          </Dropdown>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Order type</FilterLabel>
          <Dropdown ref={orderRef}>
            <DropdownTrigger
              type='button'
              onClick={() => setOrderOpen((v) => !v)}
              aria-expanded={orderOpen}
            >
              {orderTypeLabel}
              <img src={IconChevronDown} alt='' />
            </DropdownTrigger>
            {orderOpen && (
              <DropdownMenu>
                {ORDER_TYPE_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.id}
                    type='button'
                    className={cn({isActive: opt.id === tabIndex})}
                    onClick={() => applyOrderType(opt.id)}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            )}
          </Dropdown>
        </FilterGroup>
      </FiltersRow>

      <InsightsCard>
        <CardHeader>
          <CardTitle>Survey Performance</CardTitle>
          <CardSubtitle>
            How your post-order review surveys are performing.
          </CardSubtitle>
        </CardHeader>
        <PerformanceMetrics>
          <InfoMetric
            label='SMS sent'
            value={SelectedTypeSurveysCount.toLocaleString()}
            tooltip={TOOLTIPS.smsSent}
          />
          <InfoMetric
            label='Views'
            value={Views.toLocaleString()}
            tooltip={TOOLTIPS.views}
          />
          <InfoMetric
            label='Completed'
            value={CompletedSurveys.toLocaleString()}
            tooltip={TOOLTIPS.completed}
          />
          <InfoMetric
            label='Completion rate'
            value={`${completionRate}%`}
            tooltip={TOOLTIPS.completionRate}
          />
          <InfoMetric
            label='GMB review clicks'
            value={GMBReviewClicks.toLocaleString()}
            tooltip={TOOLTIPS.gmb}
          />
        </PerformanceMetrics>
      </InsightsCard>

      <TwoColumn>
        <InsightsCard>
          <SatisfactionHeader>
            <CardHeader>
              <CardTitle>Overall Rating</CardTitle>
              <CardSubtitle>
                Average star rating for delivery and pickup orders.
              </CardSubtitle>
            </CardHeader>
            <RatingBadge>
              <img src={IconAverageRating} alt='' />
              {AverageSatisfactionRate
                ? AverageSatisfactionRate.toFixed(1)
                : '0.0'}
            </RatingBadge>
          </SatisfactionHeader>
          <BarChart>
            {starRows.map((s) => (
              <StarBar key={s.label}>
                <StarBarPercent>{s.percent}%</StarBarPercent>
                <StarBarCount>{s.count.toLocaleString()}</StarBarCount>
                <StarBarTrack>
                  <StarBarFill
                    style={{height: `${s.percent}%`}}
                    className={cn({isGreen: s.label === 5})}
                  />
                </StarBarTrack>
                <StarBarLabel>{s.label}</StarBarLabel>
              </StarBar>
            ))}
          </BarChart>
        </InsightsCard>

        {isAllOrderType && (
          <InsightsCard>
            <CardHeader>
              <CardTitle>Most Popular Answers</CardTitle>
              <CardSubtitle>
                Top answers from both delivery and pickup surveys.
              </CardSubtitle>
            </CardHeader>
            {answers.length === 0 ? (
              <EmptyMessage>The first stats will come in soon</EmptyMessage>
            ) : (
              <>
                <AnswersList>
                  {answers.map((answer) => {
                    const percent =
                      totalAnswers > 0
                        ? Math.round((answer.Count / totalAnswers) * 100)
                        : 0;
                    const isGood = answer.Type === 0;
                    const surveyLabel =
                      answer.SurveyType === 0 ? 'Pickup' : 'Delivery';
                    return (
                      <AnswerRow
                        key={`${answer.Text}-${answer.SurveyType}-${answer.Type}`}
                      >
                        <AnswerDot
                          className={cn({isGreen: isGood, isRed: !isGood})}
                        />
                        <AnswerContentRow>
                          <AnswerText>
                            {answer.Text} ({surveyLabel})
                          </AnswerText>
                          <AnswerBarTrack>
                            <AnswerBarFill
                              style={{width: `${percent}%`}}
                              className={cn({isGreen: isGood, isRed: !isGood})}
                            />
                          </AnswerBarTrack>
                        </AnswerContentRow>
                        <AnswerCount>
                          {(answer.Count || 0).toLocaleString()}
                        </AnswerCount>
                      </AnswerRow>
                    );
                  })}
                </AnswersList>
                <Legend>
                  <LegendItem>
                    <LegendDot className='isGreen' />
                    Great job! Keep it up
                  </LegendItem>
                  <LegendItem>
                    <LegendDot className='isRed' />
                    Needs improvement
                  </LegendItem>
                </Legend>
              </>
            )}
          </InsightsCard>
        )}
      </TwoColumn>

      {feedbackByRating && (
        <>
          <SectionTitle>Reviews by Rating</SectionTitle>
          <TwoColumn>
            <RatingSubCard
              title='1-3 Stars - Oh no!'
              description='Please share what went wrong?'
              total={BadRankingTotal}
              items={feedbackByRating.bad}
            />
            <RatingSubCard
              title='4-5 Stars - Alright!'
              description='What did you like best?'
              total={GoodRankingTotal}
              items={feedbackByRating.good}
            />
          </TwoColumn>
        </>
      )}
    </InsightsRoot>
  );
};

const InsightsTab = () => {
  const activeLocation = useSelector((state) => state.user?.activeLocation);
  const hasReceivedSurveys = activeLocation?.hasReceivedSurveys;
  const isSurveyActivated = activeLocation?.isSurveyActivated;

  if (hasReceivedSurveys) return <InsightsContent />;
  if (!isSurveyActivated) {
    return (
      <FeedbackInner>
        <ActivateContainer />
      </FeedbackInner>
    );
  }
  return (
    <FeedbackInner>
      <SoonContainer />
    </FeedbackInner>
  );
};

export default InsightsTab;
